import {
  Award,
  Briefcase,
  FileText,
  GraduationCap,
  Image,
  Languages,
  LayoutTemplate,
  User,
  Wrench,
} from 'lucide-react'
import { type ReactNode, useCallback } from 'react'
import { useI18n } from '../../i18n'
import { usePlatformConfig } from '../../providers/platform-provider'
import type { TemplateConfig } from '../../types/adapter'
import type {
  CV,
  CVCertification,
  CVCustomSection,
  CVEducation,
  CVExperience,
  CVLanguage,
  CVPersonalInfo,
  CVSkill,
  UpdateCVRequest,
} from '../../types/cv'
import { cn } from '../ui/utils'
import { CertificationsEditor } from './certifications-editor'
import { CollapsibleSection } from './collapsible-section'
import { CustomSectionsEditor } from './custom-sections-editor'
import { EducationEditor } from './education-editor'
import { ExperienceEditor } from './experience-editor'
import { LanguagesEditor } from './languages-editor'
import { PersonalInfoEditor } from './personal-info-editor'
import { ProfilePhotoEditor } from './profile-photo-editor'
import { SkillsEditor } from './skills-editor'
import './editor.css'

export interface CVEditorProps {
  cv: CV
  onChange: (data: UpdateCVRequest) => void
  collapseAllSignal?: number
  className?: string
}

type SectionId =
  | 'layout'
  | 'photo'
  | 'personal'
  | 'experience'
  | 'education'
  | 'skills'
  | 'languages'
  | 'certifications'
  | 'custom'

interface Section {
  id: SectionId
  titleKey: string
  icon: ReactNode
  defaultExpanded: boolean
}

const DEFAULT_SECTIONS: Section[] = [
  {
    id: 'layout',
    titleKey: 'editor.sections.layout',
    icon: <LayoutTemplate className="size-4" />,
    defaultExpanded: true,
  },
  {
    id: 'photo',
    titleKey: 'editor.sections.photo',
    icon: <Image className="size-4" />,
    defaultExpanded: true,
  },
  {
    id: 'personal',
    titleKey: 'editor.sections.personal',
    icon: <User className="size-4" />,
    defaultExpanded: true,
  },
  {
    id: 'experience',
    titleKey: 'editor.sections.experience',
    icon: <Briefcase className="size-4" />,
    defaultExpanded: true,
  },
  {
    id: 'education',
    titleKey: 'editor.sections.education',
    icon: <GraduationCap className="size-4" />,
    defaultExpanded: true,
  },
  {
    id: 'skills',
    titleKey: 'editor.sections.skills',
    icon: <Wrench className="size-4" />,
    defaultExpanded: false,
  },
  {
    id: 'languages',
    titleKey: 'editor.sections.languages',
    icon: <Languages className="size-4" />,
    defaultExpanded: false,
  },
  {
    id: 'certifications',
    titleKey: 'editor.sections.certifications',
    icon: <Award className="size-4" />,
    defaultExpanded: false,
  },
  {
    id: 'custom',
    titleKey: 'editor.sections.custom',
    icon: <FileText className="size-4" />,
    defaultExpanded: false,
  },
]

/**
 * Main CV editor component
 * Contains all section editors with collapsible sections
 */
export function CVEditor({ cv, onChange, collapseAllSignal, className = '' }: CVEditorProps) {
  const { t } = useI18n()
  const config = usePlatformConfig()

  const handlePersonalInfoChange = useCallback(
    (data: Partial<CVPersonalInfo>) => {
      onChange({
        personalInfo: { ...cv.personalInfo, ...data },
      })
    },
    [cv.personalInfo, onChange]
  )

  const handleExperiencesChange = useCallback(
    (experiences: CVExperience[]) => {
      onChange({ experiences })
    },
    [onChange]
  )

  const handleEducationChange = useCallback(
    (education: CVEducation[]) => {
      onChange({ education })
    },
    [onChange]
  )

  const handleSkillsChange = useCallback(
    (skills: CVSkill[]) => {
      onChange({ skills })
    },
    [onChange]
  )

  const handleLanguagesChange = useCallback(
    (languages: CVLanguage[]) => {
      onChange({ languages })
    },
    [onChange]
  )

  const handleCertificationsChange = useCallback(
    (certifications: CVCertification[]) => {
      onChange({ certifications })
    },
    [onChange]
  )

  const handleCustomSectionsChange = useCallback(
    (customSections: CVCustomSection[]) => {
      onChange({ customSections })
    },
    [onChange]
  )

  const handleTemplateChange = useCallback(
    (templateId: string) => {
      onChange({ templateId })
    },
    [onChange]
  )

  const handleAccentColorChange = useCallback(
    (accentColor: string | null) => {
      onChange({ accentColor: accentColor ?? undefined })
    },
    [onChange]
  )

  const renderSectionContent = (sectionId: SectionId) => {
    switch (sectionId) {
      case 'layout':
        return (
          <CVTemplateSelector
            templates={config.templates}
            value={cv.templateId}
            onChange={handleTemplateChange}
            accentColor={cv.accentColor}
            onAccentColorChange={handleAccentColorChange}
          />
        )
      case 'photo':
        return <ProfilePhotoEditor data={cv.personalInfo} onChange={handlePersonalInfoChange} />
      case 'personal':
        return <PersonalInfoEditor data={cv.personalInfo} onChange={handlePersonalInfoChange} />
      case 'experience':
        return <ExperienceEditor data={cv.experiences} onChange={handleExperiencesChange} />
      case 'education':
        return <EducationEditor data={cv.education} onChange={handleEducationChange} />
      case 'skills':
        return <SkillsEditor data={cv.skills} onChange={handleSkillsChange} />
      case 'languages':
        return <LanguagesEditor data={cv.languages} onChange={handleLanguagesChange} />
      case 'certifications':
        return (
          <CertificationsEditor data={cv.certifications} onChange={handleCertificationsChange} />
        )
      case 'custom':
        return (
          <CustomSectionsEditor data={cv.customSections} onChange={handleCustomSectionsChange} />
        )
    }
  }

  return (
    <div className={cn('cv-editor flex flex-col gap-3 p-4', className)}>
      {DEFAULT_SECTIONS.map((section) => {
        const sectionTitle = t(section.titleKey)

        return (
          <div key={section.id} className="relative">
            <CollapsibleSection
              title={sectionTitle}
              icon={section.icon}
              defaultExpanded={section.defaultExpanded}
              collapseSignal={collapseAllSignal}
            >
              {renderSectionContent(section.id)}
            </CollapsibleSection>
          </div>
        )
      })}
    </div>
  )
}

const ACCENT_PRESETS = [
  { key: 'slate', color: '#334155' },
  { key: 'blue', color: '#2563eb' },
  { key: 'teal', color: '#0f766e' },
  { key: 'rose', color: '#be123c' },
  { key: 'violet', color: '#7c3aed' },
  { key: 'amber', color: '#b45309' },
] as const

interface CVTemplateSelectorProps {
  templates: TemplateConfig[]
  value: string
  onChange: (templateId: string) => void
  accentColor?: string
  onAccentColorChange: (color: string | null) => void
}

function CVTemplateSelector({
  templates,
  value,
  onChange,
  accentColor,
  onAccentColorChange,
}: CVTemplateSelectorProps) {
  const { t } = useI18n()

  return (
    <>
      <fieldset className="cv-layout-selector">
        <legend className="editor-sr-only">{t('editor.layout.selector')}</legend>
        <div className="cv-layout-options">
          {templates.map((template) => {
            const name = resolveTemplateText(t, template.id, 'name', template.name)
            const description = resolveTemplateText(
              t,
              template.id,
              'description',
              template.description
            )

            return (
              <label
                key={template.id}
                className="cv-layout-option"
                data-selected={value === template.id ? 'true' : undefined}
              >
                <input
                  type="radio"
                  name="cv-template"
                  value={template.id}
                  checked={value === template.id}
                  onChange={() => onChange(template.id)}
                />
                <span className="cv-layout-preview" data-template={template.id} aria-hidden="true">
                  <span className="cv-layout-preview-header" />
                  <span className="cv-layout-preview-row" />
                  <span className="cv-layout-preview-row" />
                  <span className="cv-layout-preview-row" />
                </span>
                <span className="cv-layout-option-copy">
                  <span className="cv-layout-option-name">{name}</span>
                  <span className="cv-layout-option-description">{description}</span>
                </span>
              </label>
            )
          })}
        </div>
      </fieldset>

      <fieldset className="cv-accent-picker">
        <legend className="cv-accent-picker-label">{t('editor.layout.accentColor')}</legend>
        <div className="cv-accent-presets">
          {ACCENT_PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              className="cv-accent-swatch"
              style={{ '--swatch-color': preset.color } as React.CSSProperties}
              aria-label={t(`editor.layout.accentPresets.${preset.key}`)}
              aria-pressed={accentColor === preset.color}
              onClick={() =>
                onAccentColorChange(accentColor === preset.color ? null : preset.color)
              }
            />
          ))}
          <label className="cv-accent-custom" aria-label={t('editor.layout.customColor')}>
            <input
              type="color"
              value={accentColor ?? '#334155'}
              onChange={(e) => onAccentColorChange(e.target.value)}
            />
          </label>
          {accentColor && (
            <button
              type="button"
              className="cv-accent-reset"
              onClick={() => onAccentColorChange(null)}
              aria-label={t('editor.layout.resetColor')}
            >
              ↺
            </button>
          )}
        </div>
      </fieldset>
    </>
  )
}

function resolveTemplateText(
  t: (key: string) => string,
  templateId: string,
  field: 'name' | 'description',
  fallback: string
): string {
  const key = `templates.${templateId}.${field}`
  const translated = t(key)
  return translated === key ? fallback : translated
}
